/**
 * Swift protocol generator - Functional implementation with dependency injection
 * Generates Swift protocols from parsed module definitions
 */

import type {
  InterfaceDefinition,
  MethodParameter,
  ModuleConstant,
  ModuleMethod,
  ParsedModule,
} from "../../parser";
import { isAnyObjectType } from "../../utils/type-helpers";
import type { SwiftCodeEmitter } from "./swift-code-emitter";
import type { SwiftTypeMapper } from "./swift-type-mapper";

export interface SwiftProtocolGeneratorDeps {
  typeMapper: SwiftTypeMapper;
  codeEmitter: SwiftCodeEmitter;
}

export interface SwiftProtocolGenerator {
  /**
   * Generates a complete Swift protocol for a module
   */
  generateModuleProtocol(
    module: ParsedModule,
    includeImports?: boolean,
  ): string;

  /**
   * Generates a protocol method signature
   */
  generateProtocolMethod(
    method: ModuleMethod,
    moduleName: string,
    interfaces?: InterfaceDefinition[],
  ): string;

  /**
   * Generates a protocol constant property
   */
  generateProtocolConstant(constant: ModuleConstant, moduleName: string): string;

  /**
   * Generates type aliases for a module
   */
  generateModuleTypeAliases(module: ParsedModule): string;
}

/**
 * Creates a Swift protocol generator with injected dependencies
 */
export function createSwiftProtocolGenerator(
  deps: SwiftProtocolGeneratorDeps,
): SwiftProtocolGenerator {
  const { typeMapper, codeEmitter } = deps;

  /**
   * Generates a complete Swift protocol for a module
   */
  function generateModuleProtocol(
    module: ParsedModule,
    includeImports: boolean = true,
  ): string {
    const protocolMethods = module.methods
      .map((method) =>
        generateProtocolMethod(method, module.moduleName, module.interfaceDefinitions),
      )
      .join("\n\n");

    // Generate constants as computed properties
    const constantProperties =
      module.constants.length > 0
        ? module.constants
            .map((constant) =>
              generateProtocolConstant(constant, module.moduleName),
            )
            .join("\n\n")
        : "";

    const constantsSection =
      module.constants.length > 0
        ? `\n    ${codeEmitter.generateMarkComment("Module Constants")}\n    \n${codeEmitter.indent(constantProperties)}\n`
        : "";

    const protocolContent = `/**
 * Generated protocol for ${module.moduleName} module
 * Implement this protocol to create a type-safe ${module.moduleName} module
 */
public protocol ${module.moduleName}Spec {${constantsSection}

    ${codeEmitter.generateMarkComment("Module Methods")}

${codeEmitter.indent(protocolMethods)}
}`;

    if (!includeImports) {
      return protocolContent;
    }

    return codeEmitter.generateSwiftFile(
      ["Foundation", "BrickModule"],
      protocolContent,
    );
  }

  /**
   * Generates a protocol method signature
   */
  function generateProtocolMethod(
    method: ModuleMethod,
    moduleName: string,
    interfaces: InterfaceDefinition[] = [],
  ): string {
    const params = method.params
      .map((param) =>
        generateProtocolParameter(param, moduleName, interfaces),
      )
      .join(", ");

    const returnType = typeMapper.mapTypeScriptToSwiftWithInterfaces(method.returnType);

    const asyncKeyword = method.isAsync ? "async " : "";
    const comment = method.jsDocComment
      ? `${codeEmitter.generateSwiftMultilineDocComment(method.jsDocComment.split('\n'))}\n`
      : "";

    // Omit "-> Void" in protocol methods; just use throws/async throws
    const isVoidReturn = returnType === "Void";
    const throwsAndReturn = isVoidReturn ? "throws" : `throws -> ${returnType}`;

    return `${comment}${codeEmitter.generateSwiftDocComment(`${method.name} method`)}
func ${method.name}(${params}) ${asyncKeyword}${throwsAndReturn}`;
  }

  /**
   * Generates a protocol parameter
   */
  function generateProtocolParameter(
    param: MethodParameter,
    moduleName: string,
    interfaces: InterfaceDefinition[] = [],
  ): string {
    // If parser marked this as a function/callback, emit closure type using parameter types
    if (param.functionType) {
      const argTypes = (param.functionType.paramTypes || []).map(
        (t: string, idx: number) => {
          const tt = (t || "").trim();
          // Arrays in callback args
          if (tt.endsWith("[]")) {
            const elem = tt.slice(0, -2).trim();
            // Special-case: JS callback where a param is a rest any[] (e.g., (...args: any[]))
            if (param.functionType?.paramRestFlags?.[idx] && elem === "any") {
              return "[Any]";
            }
          }
          return typeMapper.mapTsToSwiftForCallbackArg(tt);
        },
      );
      const argJoined = argTypes.join(", ");
      // Protocol closure parameters should be escaping for async usage
      return `${param.name}: @escaping (${argJoined}) -> Void`;
    }

    // For protocol surface, `{}` and `any` should be `[String: Any]`
    const t = (param.type || "").trim();
    if (isAnyObjectType(t)) {
      return `${param.name}: [String: Any]`;
    }

    // Default mapping including interface recognition
    const swiftType = typeMapper.mapTypeScriptToSwiftWithInterfaces(t);
    return `${param.name}: ${swiftType}`;
  }

  /**
   * Generates a protocol constant property
   */
  function generateProtocolConstant(
    constant: ModuleConstant,
    moduleName: string,
  ): string {
    const swiftType = typeMapper.mapTypeScriptToSwift(constant.type);
    const comment = codeEmitter.generateSwiftDocComment(`${constant.name} constant`);
    return `${comment}\nvar ${constant.name}: ${swiftType} { get }`;
  }

  /**
   * Generates type aliases for a module
   */
  function generateModuleTypeAliases(module: ParsedModule): string {
    const aliases: string[] = [];

    // Get all interface definitions for this module
    module.interfaceDefinitions.forEach((interfaceDef) => {
      const originalName = interfaceDef.name;
      const prefixedName = `${module.moduleName}${originalName}`;
      aliases.push(
        codeEmitter.generateSwiftTypeAlias(
          originalName,
          prefixedName,
          true,
          `Type alias for ${originalName} from ${module.moduleName} module`,
        ),
      );
    });

    return aliases.join("\n");
  }

  return {
    generateModuleProtocol,
    generateProtocolMethod,
    generateProtocolConstant,
    generateModuleTypeAliases,
  };
}
